<script setup>
import {ref, computed, reactive, watch} from 'vue'
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { ShieldCheckIcon, XIcon, ShoppingCartIcon } from '@heroicons/vue/outline'
import { CheckIcon, StarIcon } from '@heroicons/vue/solid'
import DateTimePicker from "../../Shared/Forms/DateTimePicker";
import {usePage} from "@inertiajs/inertia-vue3";
import Cart from "../../Shared/Cart";
import emitter from 'tiny-emitter/instance'
import CartItem from "../../Shared/CartItem";
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/themes/airbnb.css'
import { Dutch } from "flatpickr/dist/l10n/nl.js"
import addDays from 'date-fns/addDays'

const themeColor = computed(() => usePage().props.value.theme_color);

const props = defineProps({
  modelValue: Boolean,
  product: Object,
  disabledDates: Array,
})

const emit = defineEmits(['update:modelValue', 'showCart'])

let pickupDate = ref(null);
let dropOffDate = ref(null);
let pickupTime = ref(null);
let available = ref( true);
let error = ref( null);

let pickupPickrConfig = reactive( {
  disable: props.disabledDates,
  minDate: 'today'
})

let dropOffPickrConfig = reactive( {
  disable: props.disabledDates,
  minDate: null,
  maxDate: null,
})

let timePickrConfig = reactive( {
  dateFormat: "H:i",
  altFormat: "H:i",
  minTime: null,
  maxTime: null,
  noCalendar: true,
  enableTime: true,
  locale:  Dutch,
})

const addCartItem = (id, name, price, imageSrc, href, pickupDate, dropOffDate, pickupTime) => {
  const cart = new Cart();
  const cartItem = new CartItem(id, name, price, imageSrc, href, pickupDate, dropOffDate, pickupTime);
  cart.addItem(cartItem)

  closePanel()
  emitter.emit('updateCart')
}

const closePanel = (event) => {
  pickupDate.value = null;
  dropOffDate.value = null;
  pickupTime.value = null;
  error.value = null;
  emit('update:modelValue', false)
}

function checkAvailability() {
  if(pickupDate.value !== null) {
    axios
      .post(route('rentable.check', { rentable: props.product.slug }), {
        pickupDate: pickupDate.value,
        dropOffDate: dropOffDate.value,
        pickupTime: pickupTime.value,
      })
      .then(function(response) {
        available.value = response.data.available;
      })
  }
}

function checkOpeningHours() {
  if(pickupDate.value) {
    axios
      .post(route('opening-hours', { rentable: props.product.slug }), {
        pickupDate: pickupDate.value,
      })
      .then(function(response) {
        timePickrConfig.minTime = response.data.start;
        timePickrConfig.maxTime = response.data.end;
      })
  }
}

function handleRentingBoundries() {
  dropOffDate.value = null;
  setDropOffMinDate();
  setDropOffMaxDate();
  checkOpeningHours();
}

function validateForm() {
  if(available.value &&
    pickupDate.value &&
    dropOffDate.value &&
    pickupTime.value) {
    addCartItem(props.product.id,
      props.product.name,
      props.product.price,
      props.product.image,
      route('rentable.detail', props.product.slug), pickupDate.value, dropOffDate.value, pickupTime.value)
  } else {
    error.value = 'Helaas het product kon niet worden toegevoegd aan uw winkelmandje.'
  }
}

function setDropOffMinDate() {
  if(pickupDate.value) {
    const minDate = new Date(pickupDate.value);
    const days = props.product.minimum_days ?? 1
    const date = addDays(minDate, days)
    dropOffPickrConfig.minDate = date.toISOString().split('T')[0];
  }
}

function setDropOffMaxDate() {
  if(props.product.maximum_days && pickupDate.value) {
    const maxDate = new Date(pickupDate.value);
    const date = addDays(maxDate, props.product.maximum_days)
    dropOffPickrConfig.maxDate = date.toISOString().split('T')[0]
  }
}

</script>

<template>
  <TransitionRoot as="template" :show="modelValue">
    <Dialog as="div" class="relative z-10" @close="closePanel">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="hidden fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity md:block" />
      </TransitionChild>

      <div class="fixed z-10 inset-0 overflow-y-auto">
        <div class="flex min-h-screen text-center md:block md:px-2 lg:px-4" style="font-size: 0">
          <!-- This element is to trick the browser into centering the modal contents. -->
          <span class="hidden md:inline-block md:align-middle md:h-screen" aria-hidden="true">&#8203;</span>
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 md:translate-y-0 md:scale-95" enter-to="opacity-100 translate-y-0 md:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 md:scale-100" leave-to="opacity-0 translate-y-4 md:translate-y-0 md:scale-95">
            <DialogPanel class="flex text-base text-left transform transition w-full md:inline-block md:max-w-2xl md:px-4 md:my-8 md:align-middle lg:max-w-4xl">
              <div class="w-full relative flex items-center bg-white px-4 pt-14 pb-8 overflow-hidden shadow-2xl sm:px-6 sm:pt-8 md:p-6 lg:p-8">

                <button type="button" class="absolute top-4 right-4 text-gray-400 hover:text-gray-500 sm:top-8 sm:right-6 md:top-6 md:right-6 lg:top-8 lg:right-8" @click="closePanel">
                  <span class="sr-only">Close</span>
                  <XIcon class="h-6 w-6" aria-hidden="true" />
                </button>

                <div class="w-full grid grid-cols-1 gap-y-8 gap-x-6 items-start sm:grid-cols-12 lg:gap-x-8">
                  <div class="sm:col-span-4 lg:col-span-5">
                    <div class="aspect-w-1 aspect-h-1 rounded-lg bg-gray-100 overflow-hidden">
                      <img :src="product.image || product.imageSrc" class="object-center object-cover" />
                    </div>
                  </div>
                  <div class="sm:col-span-8 lg:col-span-7">
                    <h2 class="text-2xl font-extrabold text-gray-900 sm:pr-12">{{ product.name }}</h2>

                    <section aria-labelledby="information-heading" class="mt-4">
                      <h3 id="information-heading" class="sr-only">Product informatie</h3>

                      <div class="flex items-center">
                        <p class="text-lg text-gray-900 sm:text-xl">€{{ product.price }} p/d</p>

                        <div class="ml-4 pl-4 border-l border-gray-300">
                          <h4 class="sr-only">Reviews</h4>
                          <div class="flex items-center">
                            <div class="flex items-center">
                              <StarIcon v-for="rating in [0, 1, 2, 3, 4]" :key="rating" :class="[product.rating > rating ? 'text-yellow-400' : 'text-gray-300', 'h-5 w-5 flex-shrink-0']" aria-hidden="true" />
                            </div>
                            <p class="sr-only">{{ product.rating }} van de 5 sterren</p>
                          </div>
                        </div>
                      </div>

                    </section>

                    <section aria-labelledby="options-heading" class="mt-6">
                      <h3 id="options-heading" class="sr-only">Product opties</h3>

                      <form>
                        <div class="flex-row justify-between">
                          <div class="mt-2">
                            <label for="pickup_date" class="block text-sm font-medium text-gray-700 mb-2"> Ophaaldatum </label>
                            <flatPickr
                              id="pickup_date"
                              :config="pickupPickrConfig"
                              @on-change="handleRentingBoundries()"
                              required
                              class="focus:ring-primary focus:border-primary flex-grow block w-full min-w-0 rounded-md sm:text-sm border-gray-300"
                              :refString="'pickupDatePicker'"
                              v-model="pickupDate"
                              placeholder="Ophaaldatum"
                            />
                            <p v-if="false" class="mt-1 text-xs text-red-500">De ophaal datum mag niet in het verleden liggen.</p>

                          </div>

                          <div class="mt-2">
                            <label for="pickup_date" class="block text-sm font-medium text-gray-700 mb-2"> Retourdatum </label>
                            <flatPickr
                              id="pickup_date"
                              :config="dropOffPickrConfig"
                              @on-change="checkAvailability()"
                              required
                              class="focus:ring-primary focus:border-primary flex-grow block w-full min-w-0 rounded-md sm:text-sm border-gray-300"
                              :refString="'dropOffDatePicker'"
                              v-model="dropOffDate"
                              placeholder="Retourdatum"
                            />
                            <p v-if="!available" class="mt-1 text-xs text-red-500">Er is helaas geen product beschikbaar in deze periode.</p>

                          </div>

                          <div class="mt-2 mt-4">
                            <label for="pickup_time" class="block text-sm font-medium text-gray-700 mb-2"> Ophaaltijd </label>

                            <flatPickr
                              id="pickup_time"
                              :config="timePickrConfig"
                              class="focus:ring-primary focus:border-primary flex-grow block w-full min-w-0 rounded-md sm:text-sm border-gray-300"
                              v-model="pickupTime"
                              placeholder="Kies ophaaltijd"
                            />
                          </div>

                        </div>

                        <div class="mt-6">
                          <button @click="validateForm"
                                  type="button"
                                  :style="{'background-color': themeColor}"
                                  class="w-full border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-primary">
                            <ShoppingCartIcon class="flex-shrink-0 h-6 w-6 text-white group-hover:text-gray-300 mr-3" aria-hidden="true" />
                            Toevoegen aan bestelling
                          </button>
                          <p v-if="error" class="mt-1 text-sm text-red-500">{{ error }}</p>
                        </div>
                      </form>
                    </section>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
